import { z } from 'zod'
import { parseChart, renderChart } from './chart.ts'
import { normaliseCatalogTitle } from './catalog-match.ts'
import { isThemeSlug, SERMON_THEME_LIMIT, themeSlug, vocabularyThemes } from './themes.ts'

/**
 * AI song suggestions from the sermon (docs/15-MUSIC-AND-AI.md, "AI
 * suggestions"): the pure parts — what a call costs (for the operators'
 * margin; churches spend credits, ai-credits.ts), what a sermon's extraction
 * holds (which files can carry a sermon: sermon-files.ts), a song's first
 * lines, themes, and which songs of a large library are worth putting in
 * front of the model.
 *
 * Nothing the model says is applied by itself: suggestions are shown, a
 * person adds them. Every song from outside the library is a real recording,
 * found in the catalog, or it is not shown at all.
 */

// ---- what a call costs, and the month ------------------------------------------------

/**
 * AI costs are integer micro-dollars (a millionth of a US dollar): a call
 * costs fractions of a cent, and integers add up exactly.
 */
export const MICROS_PER_USD = 1_000_000

/** A provider's reported cost (US dollars, a float) as micro-dollars, rounded up. */
export function usdToMicros(usd: number): number {
  if (!Number.isFinite(usd) || usd <= 0) return 0
  return Math.ceil(usd * MICROS_PER_USD - 1e-6)
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** "$1.23"; a cost under a cent but above nothing is "<$0.01". */
export function formatMicros(micros: number): string {
  if (micros > 0 && micros < MICROS_PER_USD / 100) return '<$0.01'
  return usd.format(micros / MICROS_PER_USD)
}

const usdFine = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

/** "$0.0123": to a hundredth of a cent, for the operators' cost per credit. */
export function formatMicrosFine(micros: number): string {
  return usdFine.format(micros / MICROS_PER_USD)
}

/** The first moment of `date`'s month, UTC: credits run by calendar month. */
export function utcMonthStart(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
}

/** The first moment of the next month, UTC: when credits reset. */
export function utcNextMonthStart(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1))
}

// ---- the sermon -------------------------------------------------------------------

export const SERMON_STATES = ['empty', 'processing', 'ready', 'failed'] as const
export type SermonState = (typeof SERMON_STATES)[number]

export const SUGGESTION_STATES = ['none', 'processing', 'ready', 'failed'] as const
export type SuggestionsState = (typeof SUGGESTION_STATES)[number]

/** What the model reads from a sermon: the shape its answer must take. */
export interface SermonExtraction {
  /** Two or three sentences. */
  summary: string
  /** "Incarnation", "Light": a handful, the vocabulary's labels (themes.ts). */
  themes: string[]
  /** The same themes as vocabulary slugs: what the platform counts. */
  themeSlugs: string[]
  /** Every passage it preaches from, as written ("John 1:14-18"). */
  passages: string[]
  /** The sermon's main points, one line each. */
  keyIdeas: string[]
  /** "Reflective, hopeful". */
  tone: string
}

const text = (max: number) => z.string().trim().max(max)

export const sermonExtractionSchema = z.object({
  summary: text(1200),
  themes: z.array(text(60)).max(12),
  themeSlugs: z.array(z.string()).max(12).optional(),
  passages: z.array(text(80)).max(20),
  keyIdeas: z.array(text(300)).max(10),
  tone: text(120),
})

/**
 * A stored extraction, read safely (null: not one). Its slugs are the
 * vocabulary's only; one stored without them has them read from its labels.
 */
export function parseSermonExtraction(value: unknown): SermonExtraction | null {
  const parsed = sermonExtractionSchema.safeParse(value)
  if (!parsed.success) return null
  const themes = normaliseThemes(parsed.data.themes, 8)
  const slugs = parsed.data.themeSlugs ?? themes.flatMap((t) => themeSlug(t) ?? [])
  return {
    ...parsed.data,
    themes,
    themeSlugs: [...new Set(slugs.filter(isThemeSlug))].slice(0, SERMON_THEME_LIMIT),
    passages: [...new Set(parsed.data.passages.filter(Boolean))],
    keyIdeas: parsed.data.keyIdeas.filter(Boolean),
  }
}

/**
 * What a model answered about a sermon, with its themes read into the
 * vocabulary: each mapped through `themeSlug`, the ones that don't map
 * dropped, at most six (null: not an answer).
 */
export function parseSermonAnswer(value: unknown): SermonExtraction | null {
  const parsed = parseSermonExtraction(value)
  if (!parsed) return null
  return { ...parsed, ...vocabularyThemes(parsed.themes) }
}

/** Passages as typed: "John 1:14-18; Isaiah 9:2" → each on its own, trimmed, no repeats. */
export function parsePassages(input: string | readonly string[]): string[] {
  const parts = typeof input === 'string' ? input.split(/[;\n]/) : input
  return [
    ...new Set(parts.map((p) => p.replace(/\s+/g, ' ').trim()).filter((p) => p !== '')),
  ].slice(0, 20)
}

// ---- songs as the model reads them ---------------------------------------------------

/** A song's first sung lines from its ChordPro, chords and headings left out. */
export function chartFirstLines(source: string | null, maxChars = 160): string {
  if (!source?.trim()) return ''
  const sheet = renderChart(parseChart(source), { mode: 'lyrics' })
  const out: string[] = []
  let length = 0
  for (const line of sheet.lines) {
    if (line.kind !== 'lyrics') continue
    const words = line.segments
      .map((s) => s.lyric)
      .join('')
      .replace(/\s+/g, ' ')
      .trim()
    if (!words) continue
    // Whole lines only, after the first: half a line reads as a mistake.
    if (out.length > 0 && length + words.length > maxChars) break
    out.push(words)
    length += words.length + 3
  }
  const joined = out.join(' / ')
  return joined.length > maxChars ? `${joined.slice(0, maxChars - 1).trimEnd()}…` : joined
}

/** A song's whole lyric from its ChordPro, as plain lines (theme tagging reads it). */
export function chartLyrics(source: string | null, maxChars = 1500): string {
  if (!source?.trim()) return ''
  const sheet = renderChart(parseChart(source), { mode: 'lyrics' })
  const lines = sheet.lines.flatMap((line) =>
    line.kind === 'lyrics'
      ? [
          line.segments
            .map((s) => s.lyric)
            .join('')
            .replace(/\s+/g, ' ')
            .trim(),
        ].filter(Boolean)
      : [],
  )
  const joined = lines.join('\n')
  return joined.length > maxChars ? `${joined.slice(0, maxChars)}…` : joined
}

/** A theme as a library keeps it: trimmed, single-spaced, capitalised, at most 30 characters. */
export function normaliseTheme(theme: string): string {
  const clean = theme.replace(/\s+/g, ' ').trim().slice(0, 30).trim()
  return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : ''
}

/** Themes without repeats (whatever their case), in their order, at most `max`. */
export function normaliseThemes(themes: readonly string[], max = 6): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of themes) {
    const theme = normaliseTheme(raw)
    const id = theme.toLowerCase()
    if (!theme || seen.has(id)) continue
    seen.add(id)
    out.push(theme)
    if (out.length >= max) break
  }
  return out
}

/** The suggested themes a song does not have yet (case aside). */
export function newThemes(current: readonly string[], suggested: readonly string[]): string[] {
  const have = new Set(current.map((t) => t.trim().toLowerCase()))
  return normaliseThemes(suggested).filter((t) => !have.has(t.toLowerCase()))
}

/** A song's themes with the accepted ones added (theirs first, nothing twice). */
export function withThemes(current: readonly string[], accepted: readonly string[]): string[] {
  const out = [...current]
  const have = new Set(current.map((t) => t.trim().toLowerCase()))
  for (const theme of normaliseThemes(accepted, 20))
    if (!have.has(theme.toLowerCase())) {
      have.add(theme.toLowerCase())
      out.push(theme)
    }
  return out
}

// ---- bounding the prompt ---------------------------------------------------------------

/** At most this many library songs go to the model; a larger library is narrowed first. */
export const PROMPT_LIBRARY_LIMIT = 150

const STOP_WORDS = new Set(
  'a an and are as at be but by for from has he his i in is it its me my of on or our so that the their them they this to us was we were who will with you your thy thou thee o oh lord god'.split(
    ' ',
  ),
)

/** The words of a text worth matching on: lowercase, no stop words, at least three letters. */
export function significantWords(text: string): Set<string> {
  return new Set(
    normaliseCatalogTitle(text)
      .split(' ')
      .filter((w) => w.length >= 3 && !STOP_WORDS.has(w))
      // A crude stem: "lights" and "light" are one word to a theme ("darkness" stays).
      .map((w) => (w.endsWith('ss') ? w : w.replace(/s$/, ''))),
  )
}

export interface LibrarySongForPrompt {
  id: string
  title: string
  themes: readonly string[]
  /** The first lines of its default arrangement. */
  firstLines: string
  /** When it was last on a plan (ISO date); null: never. */
  lastSungOn: string | null
}

/**
 * The songs most worth the model's attention when the library is larger than
 * `limit`: those sharing the most words with the sermon (title, themes, first
 * lines), then the less recently sung. A small library goes as it is.
 */
export function prefilterLibrary<T extends LibrarySongForPrompt>(
  songs: readonly T[],
  sermonText: string,
  limit = PROMPT_LIBRARY_LIMIT,
): T[] {
  if (songs.length <= limit) return [...songs]
  const terms = significantWords(sermonText)
  const scored = songs.map((song, index) => {
    const words = significantWords(`${song.title} ${song.themes.join(' ')} ${song.firstLines}`)
    let score = 0
    for (const word of words) if (terms.has(word)) score++
    // Themes are what a church chose to say about a song: they count double.
    for (const theme of song.themes)
      for (const w of significantWords(theme)) if (terms.has(w)) score++
    return { song, score, index }
  })
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      (a.song.lastSungOn ?? '').localeCompare(b.song.lastSungOn ?? '') ||
      a.index - b.index,
  )
  return scored.slice(0, limit).map((row) => row.song)
}

/** A suggestion's fit, as the model rates it, kept to 1–100. */
export function clampFit(fit: unknown): number {
  const n = typeof fit === 'number' && Number.isFinite(fit) ? Math.round(fit) : 50
  // A model that answers 0–1 meant a fraction.
  const scaled = n <= 1 && typeof fit === 'number' && fit > 0 && fit < 1 ? Math.round(fit * 100) : n
  return Math.min(100, Math.max(1, scaled))
}

// ---- putting a suggestion on the order ------------------------------------------------------

/**
 * Where a suggested song goes in a running order (the index it takes):
 * right after the last song of the service itself, else after the
 * service's last item, else at the very end. Before and After items
 * (a walk-in song, the postlude) are not the service.
 */
export function suggestionPlacement(
  items: readonly { songId: string | null; servicePosition: string }[],
): number {
  let lastSong = -1
  let lastDuring = -1
  items.forEach((item, index) => {
    if (item.servicePosition !== 'during') return
    lastDuring = index
    if (item.songId) lastSong = index
  })
  if (lastSong !== -1) return lastSong + 1
  if (lastDuring !== -1) return lastDuring + 1
  return items.length
}

/**
 * "Last sung 6 weeks ago": how long since a song was on a plan, in the
 * units a worship leader counts in — days, then weeks, months, years.
 * `lastSungOn` is an ISO date; null is "Never sung here".
 */
export function lastSungLine(lastSungOn: string | null, now: Date = new Date()): string {
  if (!lastSungOn) return 'Never sung here'
  const then = Date.parse(`${lastSungOn}T00:00:00Z`)
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const days = Math.max(0, Math.round((today - then) / 86_400_000))
  const ago = (n: number, unit: string) => `Last sung ${n} ${unit}${n === 1 ? '' : 's'} ago`
  if (days === 0) return 'Last sung today'
  if (days === 1) return 'Last sung yesterday'
  if (days < 14) return ago(days, 'day')
  if (days < 63) return ago(Math.round(days / 7), 'week')
  if (days < 365) return ago(Math.round(days / 30.44), 'month')
  return ago(Math.round(days / 365.25), 'year')
}
